import { FC, useMemo } from 'react'
import { capitalize } from 'lodash'
import { useLocation, useRouteMatch } from 'react-router-dom'

import { FilterButtonSkeleton, HStack, rem, Spacer } from '@revolut-internal/ui-kit'

import { Security } from 'common/security/security'
import { useAgentsQuery } from 'modules/Monitoring/api/agents/queries'
import { useAreasQuery } from 'modules/Monitoring/api/areas/queries'
import { useDepartmentsQuery } from 'modules/Monitoring/api/departments/queries'
import { useQueuesQuery } from 'modules/Monitoring/api/queues/queries'
import { useSpecialisationQuery } from 'modules/Monitoring/api/specialisations/queries'
import { CustomFilterOption } from 'modules/Monitoring/components/Filters/CustomFilterOption'
import { MultiSelectFilter } from 'modules/Monitoring/components/Filters/MultiSelectFilter'
import { RangeFilter } from 'modules/Monitoring/components/Filters/RangeFilter/RangeFilter'
import { RangeFilterValue } from 'modules/Monitoring/components/Filters/RangeFilter/RangeFilterContent'
import { TabBar } from 'modules/Monitoring/components/TabBar/TabBar'
import { Route } from 'modules/Monitoring/consts/routes'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import { QueueingSystem, QueueStatsFilterResponse } from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/Monitoring/url-state-storage/useHashedSearchParams'
import { useLegacyFiltersMigration } from 'modules/Monitoring/url-state-storage/useLegacyFiltersMigration'
import { deriveLoadingStateFromQueryStatus } from 'modules/Monitoring/utils/deriveLoadingStateFromQueryStatus'
import { getGroupedQueues } from 'modules/Monitoring/utils/getGroupedQueues'
import { getDefaultTimeRangeParams } from 'modules/Monitoring/utils/time/getDefaultTimeRangeParams'

import { AdditionalFilters } from './AdditionalFilters/AdditionalFilters'
import { useChannelsFilter } from './useChannelsFilter'

const TAB_BAR_WIDTH = rem(240)

type GlobalFiltersProps = {
  additionalFilters?: QueueStatsFilterResponse
  showAdditionalFilters?: boolean
  areAdditionalFiltersPending?: boolean
}

export type GlobalFilterState = {
  departments: string[]
  areas: string[]
  specialisations: string[]
  queues: string[]
  agents: string[]
  from: number | null
  to: number | null
  start?: string
  end?: string
}

const INITIAL_STATE: GlobalFilterState = {
  departments: [],
  areas: [],
  specialisations: [],
  queues: [],
  agents: [],
  from: null,
  to: null,
  start: undefined,
  end: undefined,
}

export const GlobalFilters: FC<GlobalFiltersProps> = ({
  additionalFilters,
  showAdditionalFilters = false,
  areAdditionalFiltersPending = false,
}) => {
  const location = useLocation()

  useLegacyFiltersMigration()

  const { channel, regionedChannel, entityName, specialisationLabel, isDante } =
    useChannel()

  const {
    state: filtersState,
    updateState,
    removeKeys,
  } = useHashedSearchParams<GlobalFilterState>({
    contextKey: regionedChannel,
    initialState: INITIAL_STATE,
    hashParamKey: UrlStateStorageKeys.GlobalFilters,
  })

  const { Filter: ChannelsFilter, isPending: isChannelsPending } = useChannelsFilter()
  const { data: departments = [], status: departmentsStatus } = useDepartmentsQuery({
    channel,
  })
  const { data: areas = [], status: areasStatus } = useAreasQuery(
    {
      channel,
    },
    { enabled: Security.canViewAreas },
  )
  const { data: specialisations = [], status: specialisationsStatus } =
    useSpecialisationQuery(
      {
        channel,
      },
      { enabled: Security.canViewSpecialisations },
    )
  const { data: queues = [], status: queuesStatus } = useQueuesQuery(
    {
      channel,
    },
    { enabled: Security.canViewQueues },
  )

  const isAgentPage = Boolean(useRouteMatch({ path: Route.Agent }))

  const { data: agents = [], status: agentsStatus } = useAgentsQuery({
    enabled: isAgentPage && Security.canViewAgents,
  })

  const defaultTimeRangeParams = useMemo(() => getDefaultTimeRangeParams(), [])

  const departmentsIds = filtersState.departments
  const areaIds = filtersState.areas
  const specialisationIds = filtersState.specialisations

  const timeRange: RangeFilterValue = {
    timeRange: {
      from: filtersState.from || defaultTimeRangeParams.from,
      to: filtersState.to || defaultTimeRangeParams.to,
    },
    start: filtersState.start || '',
    end: filtersState.end || '',
  }

  const departmentOptions = useMemo(() => {
    if (!Security.canViewDepartments) {
      return []
    }

    return departments.map(({ id, name }) => ({
      key: id,
      value: id,
      label: name,
    }))
  }, [departments])

  const areaOptions = useMemo(() => {
    if (!Security.canViewAreas) {
      return []
    }

    const ticketingAreas = areas.filter(
      ({ queueingSystem }) => queueingSystem === QueueingSystem.Ticketing,
    )
    const filteredAreas = departmentsIds?.length
      ? ticketingAreas.filter(({ departmentId }) => departmentsIds.includes(departmentId))
      : ticketingAreas

    return filteredAreas.map(({ id, name }) => ({
      key: id,
      value: id,
      label: name,
    }))
  }, [areas, departmentsIds])

  const specialisationOptions = useMemo(() => {
    if (!Security.canViewSpecialisations) {
      return []
    }

    const departmentFilteredSpecialisations = departmentsIds.length
      ? specialisations.filter(({ departmentId }) =>
          departmentsIds.includes(departmentId),
        )
      : specialisations
    const areaFilteredSpecialisations = areaIds.length
      ? departmentFilteredSpecialisations.filter(({ areaId }) => areaIds.includes(areaId))
      : departmentFilteredSpecialisations

    return areaFilteredSpecialisations.map(({ id, name }) => ({
      key: id,
      value: id,
      label: name,
    }))
  }, [specialisations, departmentsIds, areaIds])

  const queueOptions = useMemo(() => {
    if (!Security.canViewQueues) {
      return []
    }

    const selectedSpecialisations = specialisationIds.length
      ? specialisationOptions.filter(({ value }) => specialisationIds.includes(value))
      : specialisationOptions

    return getGroupedQueues(queues, selectedSpecialisations)
  }, [queues, specialisationOptions, specialisationIds])

  const agentOptions = useMemo(() => {
    if (!Security.canViewAgents) {
      return []
    }

    return agents.map(({ id, email, name }) => ({
      key: id,
      value: id,
      label: email,
      description: name,
    }))
  }, [agents])

  const handleTimeRangeChange = (range: RangeFilterValue) => {
    updateState({
      from: range.timeRange.from,
      to: range.timeRange.to,
      start: range.start,
      end: range.end,
    })
  }

  const handleFilterChange =
    (key: keyof GlobalFilterState) => (values: string[] | null) => {
      updateState({ [key]: values })
    }

  if (isChannelsPending || (showAdditionalFilters && areAdditionalFiltersPending)) {
    return <GlobalFiltersSkeleton />
  }

  return (
    <HStack space="s-8">
      <ChannelsFilter />

      {Security.canViewDepartments && (
        <MultiSelectFilter
          label="Departments"
          options={departmentOptions}
          value={filtersState.departments}
          loadingState={deriveLoadingStateFromQueryStatus(departmentsStatus)}
          onChange={handleFilterChange('departments')}
        />
      )}
      {isDante && Security.canViewAreas && (
        <MultiSelectFilter
          label="Areas"
          options={areaOptions}
          value={filtersState.areas}
          loadingState={deriveLoadingStateFromQueryStatus(areasStatus)}
          onChange={handleFilterChange('areas')}
        />
      )}
      {Security.canViewSpecialisations && (
        <MultiSelectFilter
          label={`${capitalize(specialisationLabel)}s`}
          options={specialisationOptions}
          value={filtersState.specialisations}
          loadingState={deriveLoadingStateFromQueryStatus(specialisationsStatus)}
          onChange={handleFilterChange('specialisations')}
        />
      )}
      {Security.canViewQueues && (
        <MultiSelectFilter
          label="Queues"
          options={queueOptions}
          value={filtersState.queues}
          loadingState={deriveLoadingStateFromQueryStatus(queuesStatus)}
          onChange={handleFilterChange('queues')}
        />
      )}
      {isAgentPage && Security.canViewAgents && (
        <MultiSelectFilter
          label="Agents"
          options={agentOptions}
          value={filtersState.agents}
          loadingState={deriveLoadingStateFromQueryStatus(agentsStatus)}
          renderOption={option => (
            <CustomFilterOption label={option.label} description={option.description} />
          )}
          onChange={handleFilterChange('agents')}
        />
      )}
      {isAgentPage && (
        <RangeFilter
          timeRange={timeRange}
          hasDefaultState={!filtersState.from && !filtersState.to}
          onChange={handleTimeRangeChange}
          onClear={() => removeKeys(['from', 'to', 'start', 'end'])}
        />
      )}
      {!isAgentPage && showAdditionalFilters && (
        <AdditionalFilters filters={additionalFilters?.items} />
      )}

      <Spacer />

      {Security.canViewQueueHealth && Security.canViewAgentAttendance && (
        <TabBar
          behaviourVariant="route"
          variant="segmented fit"
          width={TAB_BAR_WIDTH}
          tabs={[
            {
              to: `${Route.Ticket}${location.search}`,
              label: capitalize(entityName),
            },
            {
              to: `${Route.Agent}${location.search}`,
              label: 'Agent',
            },
          ]}
        />
      )}
    </HStack>
  )
}

const GlobalFiltersSkeleton = () => (
  <HStack space="s-8">
    <FilterButtonSkeleton />
    <FilterButtonSkeleton />
    <FilterButtonSkeleton />
    <FilterButtonSkeleton />
  </HStack>
)
